import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from './Themes/theme';
import { ThemeProvider } from './components/theme-provider';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// Initialize Sentry error tracking
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_ENV || 'development',
  tracesSampleRate: 0.1,
  replaysOnErrorSampleRate: 0.5,
  beforeSend(event) {
    if (window.location.hostname === 'localhost') return null;
    return event;
  },
});

// Combined theme provider that syncs MUI theme with shadcn dark mode
function CombinedThemeProvider({ children }) {
  const [mode, setMode] = useState('light');

  // Watch for dark class on document root
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const isDark = document.documentElement.classList.contains('dark');
          setMode(isDark ? 'dark' : 'light');
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    // Initial check
    const isDark = document.documentElement.classList.contains('dark');
    setMode(isDark ? 'dark' : 'light');

    return () => observer.disconnect();
  }, []);

  // Create MUI theme based on mode
  const muiTheme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider defaultTheme="dark" storageKey="thaklis-managers-theme">
      <CombinedThemeProvider>
        <App />
      </CombinedThemeProvider>
    </ThemeProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
